import React, { useEffect, useState } from 'react';
import { PageContainer, PageHeader } from '@/components/layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Spinner } from '@/components/ui/spinner';
import { AlertCircle, CheckCircle2, Info } from 'lucide-react';
import {
  connectNaverSmartstore,
  disconnectNaverSmartstore,
  getNaverSmartstoreStatus,
  NaverSmartstoreConnectionStatus,
} from '@/api/naverSmartstoreApi';

/**
 * 네이버 스마트스토어 연동(1단계) - 대시보드 내 API 키 입력/상태/해제 화면.
 * 카페24와 달리 앱스토어 원클릭 설치/OAuth 리다이렉트가 없다 - 판매자가
 * 네이버 커머스API센터에서 직접 발급받은 Application ID/Secret을 여기에
 * 입력하면 그 자리에서 검증 후 저장한다. NAVER_SMARTSTORE_CMS_INTEGRATION_DESIGN.md 참고.
 */
export default function NaverSmartstoreConnect() {
  const [status, setStatus] = useState<NaverSmartstoreConnectionStatus | null>(null);
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [applicationId, setApplicationId] = useState('');
  const [applicationSecret, setApplicationSecret] = useState('');
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const loadStatus = async () => {
    setLoadingStatus(true);
    try {
      const data = await getNaverSmartstoreStatus();
      setStatus(data);
    } catch {
      setError('연동 상태를 불러오는 중 오류가 발생했습니다.');
    } finally {
      setLoadingStatus(false);
    }
  };

  useEffect(() => {
    loadStatus();
  }, []);

  const handleConnect = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);

    if (!applicationId.trim() || !applicationSecret.trim()) {
      setError('Application ID와 Application Secret을 모두 입력해주세요.');
      return;
    }

    setConnecting(true);
    try {
      const data = await connectNaverSmartstore(applicationId.trim(), applicationSecret.trim());
      setStatus(data);
      setSuccess(true);
      setApplicationId('');
      setApplicationSecret('');
    } catch (err: any) {
      setError(
        err?.response?.data?.message ||
          'Application ID/Secret을 확인해 주세요. 네이버 커머스API센터에서 발급받은 값이 맞는지 다시 확인해 주세요.'
      );
    } finally {
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    setError(null);
    setSuccess(false);
    setDisconnecting(true);
    try {
      const data = await disconnectNaverSmartstore();
      setStatus(data);
    } catch {
      setError('연동 해제 중 오류가 발생했습니다.');
    } finally {
      setDisconnecting(false);
    }
  };

  const isConnected = status?.connected === true;

  return (
    <PageContainer maxWidth="full">
      <PageHeader
        title="네이버 스마트스토어 연동"
        description="네이버 커머스API센터에서 발급받은 키로 스마트스토어를 연동합니다"
      />

      <Alert>
        <Info className="w-4 h-4" />
        <AlertDescription className="text-sm">
          현재는 상품 <strong>상세설명 이미지</strong>에만 ALT 텍스트가 자동으로 반영됩니다.
          대표/추가 이미지는 네이버 API 제약상 자동 반영이 불가능합니다.
        </AlertDescription>
      </Alert>

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="w-4 h-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      {success && (
        <Alert className="border-green-200 bg-green-50">
          <CheckCircle2 className="w-4 h-4 text-green-600" />
          <AlertDescription className="text-green-700">연동이 완료됐습니다.</AlertDescription>
        </Alert>
      )}

      {loadingStatus ? (
        <div className="flex justify-center py-8">
          <Spinner size="lg" />
        </div>
      ) : isConnected ? (
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <CardTitle className="text-base">연동 상태</CardTitle>
              <Badge className="bg-green-100 text-green-700 hover:bg-green-100">연동됨</Badge>
            </div>
            <CardDescription>
              {status?.sellerName ? `스토어: ${status.sellerName}` : '연동된 스마트스토어가 있습니다.'}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {status?.connectedAt && (
              <p className="text-sm text-gray-500">
                연동일: {new Date(status.connectedAt).toLocaleString('ko-KR')}
              </p>
            )}
            <Button
              type="button"
              variant="outline"
              onClick={handleDisconnect}
              disabled={disconnecting}
            >
              {disconnecting && <Spinner size="sm" className="mr-1.5" />}
              연동 해제
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">API 키 입력</CardTitle>
            <CardDescription>
              네이버 커머스API센터(apicenter.commerce.naver.com)에서 발급받은 Application
              ID/Secret을 입력해주세요.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleConnect} className="space-y-5">
              <div className="space-y-1.5">
                <Label htmlFor="applicationId">
                  Application ID <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="applicationId"
                  type="text"
                  value={applicationId}
                  onChange={(e) => setApplicationId(e.target.value)}
                  disabled={connecting}
                  autoComplete="off"
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="applicationSecret">
                  Application Secret <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="applicationSecret"
                  type="password"
                  value={applicationSecret}
                  onChange={(e) => setApplicationSecret(e.target.value)}
                  disabled={connecting}
                  autoComplete="off"
                />
              </div>

              <div className="pt-2 border-t">
                <Button type="submit" disabled={connecting}>
                  {connecting && <Spinner size="sm" className="mr-1.5" />}
                  연동하기
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}
    </PageContainer>
  );
}
